import { RefinementCtx, z } from 'zod'

import i18n from 'modules/core/lib/i18n'

import { CustomizationField, isMandatory } from 'modules/expenses/hooks/useExpensesCustomization'

const defaultRequiredMessage = () => ({
  message: i18n.t('expenses_v2.forms.errors.required'),
})

export interface FormContext {
  customizationFields: CustomizationField[]
}

export interface SuperRefineContext {
  data: {
    payment?: string | null
    paymentMethod?: string | null
    reimbursableAmount?: number | null
    reimbursableCurrency?: string | null
    // Add other fields that might be accessed in the validation
    [key: string]: unknown
  }
  ctx: RefinementCtx
  customizationFields: CustomizationField[]
}

export const PaymentInformationSchema = ({ customizationFields }: FormContext) => {
  return z.object({
    payment: z
      .string()
      .nullish()
      .refine(
        (payment) => (isMandatory('payment', 'expense', customizationFields) ? !!payment : true),
        defaultRequiredMessage()
      ),
    paymentMethod: z
      .string()
      .nullish()
      .refine((paymentMethod) => {
        const isPaymentMethodMandatory = isMandatory(
          'payment_method',
          'expense',
          customizationFields
        )
        return isPaymentMethodMandatory ? !!paymentMethod : true
      }, defaultRequiredMessage()),
    reimbursableAmount: z.number().nullish(),
    reimbursableCurrency: z.string().nullish(),
  })
}

export const superRefinePaymentInformationSchema = (context: SuperRefineContext) => {
  validateReimbursableFields(context)
}

const validateReimbursableFields = ({ data, ctx, customizationFields }: SuperRefineContext) => {
  // Only validate reimbursable fields if payment is 'reimbursable'
  const isReimbursablePayment = data.payment === 'reimbursable'
  if (!isReimbursablePayment) {
    return
  }

  const isReimbursableAmountMandatory = isMandatory(
    'reimbursable_amount',
    'expense',
    customizationFields
  )
  const isReimbursableCurrencyMandatory = isMandatory(
    'reimbursable_currency',
    'expense',
    customizationFields
  )
  const isReimbursableAmountNullish =
    data.reimbursableAmount === null || data.reimbursableAmount === undefined
  const isReimbursableAmountPositive = data.reimbursableAmount && data.reimbursableAmount > 0

  if (isReimbursableAmountMandatory && isReimbursableAmountNullish) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: i18n.t('expenses_v2.forms.errors.reimbursable_amount.required'),
      path: ['reimbursableAmount'],
    })
    return
  }

  if (!isReimbursableAmountNullish && !isReimbursableAmountPositive) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: i18n.t('expenses_v2.forms.errors.reimbursable_amount.invalid'),
      path: ['reimbursableAmount'],
    })
    return
  }

  if (isReimbursableCurrencyMandatory && !data.reimbursableCurrency) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: i18n.t('expenses_v2.forms.errors.required'),
      path: ['reimbursableCurrency'],
    })
    return
  }
}

export type PaymentInformationSchema = z.infer<ReturnType<typeof PaymentInformationSchema>>
